import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { Z_INDICES } from '../../lib/zIndices';
import {
  DEFAULT_INVITE_SHARE_MESSAGE,
  INVITE_SHARE_MESSAGE_MAX,
  saveInviteShareMessage,
} from '../../utils/inviteShareMessage';

/**
 * Premium bottom-sheet editor for the host's invite share message.
 *
 * The message is prepended to the invite URL whenever the host taps
 * Share / Copy on any invite card. The unique invite URL itself is NOT
 * shown in the editor — it’s appended automatically at share time so a
 * single saved template works for every invite the host has ever sent.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - initialMessage: string   (empty means "not yet customised")
 *  - onSaved: (updatedUser) => void
 */
export default function InviteMessageEditor({ open, onClose, initialMessage, onSaved }) {
  // Editing draft. Falls back to the default template if the host has
  // never customised the message yet, so the field is never blank on
  // first open (that would be an intimidating empty screen).
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const seed = (initialMessage ?? '').trim();
    setDraft(seed.length ? initialMessage : DEFAULT_INVITE_SHARE_MESSAGE);
  }, [open, initialMessage]);

  const handleSave = async () => {
    if (saving) return;
    // Save exactly what the host typed. If they wipe the field back to
    // the default template we still store it as their explicit choice
    // — cheap and avoids "did it save?" ambiguity.
    setSaving(true);
    try {
      const updatedUser = await saveInviteShareMessage(draft);
      onSaved?.(updatedUser);
      toast.success('Invite message saved');
      onClose?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to save message');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = () => {
    setDraft(DEFAULT_INVITE_SHARE_MESSAGE);
  };

  const remaining = INVITE_SHARE_MESSAGE_MAX - (draft?.length || 0);

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          key="invite-msg-editor-backdrop"
          data-testid="invite-message-editor-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.42)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            zIndex: Z_INDICES?.modalOverlay ?? 10000,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
          }}
        >
          <motion.div
            key="invite-msg-editor-sheet"
            data-testid="invite-message-editor-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Edit invite message"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 260 }}
            onClick={(event) => event.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 520,
              background: 'var(--surface)',
              borderTopLeftRadius: 28,
              borderTopRightRadius: 28,
              padding: '18px 20px max(48px, calc(24px + env(safe-area-inset-bottom, 0px)))',
              boxShadow: 'var(--premium-shadow-main)',
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
              maxHeight: '90vh',
            }}
          >
            {/* Grab handle */}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <span style={{ width: 44, height: 4, borderRadius: 999, background: 'var(--card-border)' }} />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <h2
                data-testid="invite-message-editor-title"
                style={{
                  margin: 0,
                  fontFamily: "'Inria Serif', serif",
                  fontSize: 19,
                  fontWeight: 700,
                  color: 'var(--text)',
                  lineHeight: 1.1,
                }}
              >
                Edit invite message
              </h2>
              <motion.button
                type="button"
                onClick={onClose}
                data-testid="invite-message-editor-close"
                aria-label="Close editor"
                whileTap={{ scale: 0.94 }}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 999,
                  border: '1px solid var(--card-border)',
                  background: 'var(--surface-2)',
                  color: 'var(--text-2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <X size={18} />
              </motion.button>
            </div>

            <p
              data-testid="invite-message-editor-helper"
              style={{
                margin: 0,
                fontFamily: "'Inria Serif', serif",
                fontSize: 13,
                lineHeight: 1.4,
                color: 'var(--text-2)',
              }}
            >
              This message is copied to every invite you share. The unique invite link is added
              automatically on a new line below — don’t paste it here.
            </p>

            <textarea
              data-testid="invite-message-editor-textarea"
              value={draft}
              onChange={(event) => setDraft(event.target.value.slice(0, INVITE_SHARE_MESSAGE_MAX))}
              maxLength={INVITE_SHARE_MESSAGE_MAX}
              rows={9}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                borderRadius: 16,
                border: '1px solid var(--card-border)',
                background: 'var(--surface-2)',
                color: 'var(--text)',
                padding: '12px 14px',
                fontFamily: "'Inria Serif', serif",
                fontSize: 15,
                lineHeight: 1.5,
                resize: 'vertical',
                minHeight: 180,
                outline: 'none',
              }}
            />

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
                fontFamily: "'Inria Serif', serif",
                fontSize: 12,
                color: 'var(--text-3)',
              }}
            >
              <button
                type="button"
                onClick={handleResetToDefault}
                data-testid="invite-message-editor-reset"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--accent-purple-strong)',
                  fontFamily: "'Inria Serif', serif",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                Reset to default
              </button>
              <span data-testid="invite-message-editor-counter">
                {remaining} left
              </span>
            </div>

            <motion.button
              type="button"
              onClick={handleSave}
              disabled={saving}
              data-testid="invite-message-editor-save"
              className="premium-pill-button"
              whileTap={{ scale: 0.985 }}
              style={{
                width: '100%',
                padding: '13px 0',
                fontFamily: "'Inria Serif', serif",
                fontSize: 16,
                fontWeight: 700,
                cursor: saving ? 'not-allowed' : 'pointer',
                color: 'var(--accent-purple-strong)',
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? 'Saving…' : 'Save'}
            </motion.button>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
